import { useMemo } from 'react';
import {
  useGetDataForThanksForRSVPQuery,
  useGetDataForAccommodationsThanksForRSVPQuery,
  useGetDataForTravelThanksForRSVPQuery,
  useGetRegistrySettingForRsvpQuery,
} from '@graphql/generated';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import { useEventAccommodationsCheck } from '@shared/hooks/useEventAccommodationsCheck';
import { useAccommodationStepPushCohort } from './hooks/useAccommodationStepPushCohort';
import { useRsvpFeatureValue } from '@shared/hooks/useRsvpFeatureFlags';
import useGetPostRsvpCustomRedirectUrl from './hooks/useGetPostRsvpCustomRedirectUrl';
import { isEventPageVisible } from '@shared/utils/pageVisibility';

export const DEFAULT_AMPLITUDE_VALUE = 'on';

export const useThanksForRSVPController = (eventHandle: string) => {
  const queryParams = useQueryParams();
  const guestInfo = {
    name: queryParams.guestNames as string,
    email: queryParams.guestEmail as string,
    guestId: queryParams.guestId as string,
  };
  const { value: rsvpCongratulationsEcardUserIsInUSFlagValue, loading: isRsvpCongratulationsEcardUserIsInUSFlagLoading } = useFeatureValue('rsvpCongratulationsEcardUserIsInUS');
  const { value: rsvpCongratulationsEcardExperimentEnabledFlagValue, loading: isRsvpCongratulationsEcardExperimentEnabledFlagLoading } =
    useFeatureValue('rsvpCongratulationsEcardExperimentEnabled');
  const { payload: rsvpCongratulationsEventHandleExcludeListFlagPayload = [], loading: isRsvpCongratulationsEventHandleExcludeListFlagLoading } =
    useFeatureValue('rsvpCongratulationsEventHandleExcludeList');
  const rsvpCongratulationsEcardUserIsInUS = rsvpCongratulationsEcardUserIsInUSFlagValue === DEFAULT_AMPLITUDE_VALUE;
  const rsvpCongratulationsEcardExperimentEnabled = rsvpCongratulationsEcardExperimentEnabledFlagValue === DEFAULT_AMPLITUDE_VALUE;
  const { data, loading } = useGetDataForThanksForRSVPQuery({
    batchMode: 'off',
    fetchPolicy: 'no-cache',
    variables: { eventHandle },
  });
  const { data: accommodationsData, loading: accomodationsLoading } = useGetDataForAccommodationsThanksForRSVPQuery({
    batchMode: 'off',
    fetchPolicy: 'no-cache',
    variables: { eventHandle },
  });
  const { data: travelData, loading: travelLoading } = useGetDataForTravelThanksForRSVPQuery({
    batchMode: 'off',
    fetchPolicy: 'no-cache',
    variables: { eventHandle },
  });

  const eventId = accommodationsData?.eventByName?.id ?? '';

  // Calling this hook to prewarm the cache for the `SuccessScreenSwitch` component
  const eventAccommodationsCheckReturn = useEventAccommodationsCheck({ eventHandle });
  const accommodationsCohort = useAccommodationStepPushCohort(eventAccommodationsCheckReturn);
  const { data: registryData, loading: registryLoading } = useGetRegistrySettingForRsvpQuery({
    batchMode: 'off',
    skip: !eventId,
    variables: { eventId },
  });

  const hideRegistryInMessaging = registryData?.getRegistryEventData?.hideRegistryInMessaging;

  const isRsvpGuestFlowAccommodationsStepWithAccomsExperimentEnabled =
    useFeatureValue('rsvpGuestFlowAccommodationsStepWithAccoms', {
      skip: accommodationsCohort !== 'hasOwnAccommodations',
    }).value === 'treatment';
  const { getValueString } = useQueryParamHelper();
  const success = getValueString('success');
  const rsvp = getValueString('rsvp');
  const fromGiftWrap = success === 'true';
  const didRSVP = rsvp === 'all' || rsvp === 'mixed';
  const noRSVP = rsvp === 'none';

  const { messagingSettingsLoading, customRedirectUrl } = useGetPostRsvpCustomRedirectUrl({ eventId, attending: didRSVP });

  // March 11, 2025
  // The redirect experiment has been rolled out to 100% of users, but we're running a
  // new experiment where we try to show accommodations in the RSVP flow (not redirect)
  const { value: redirectExperiment } = useRsvpFeatureValue('rsvpEnableAccommodationsRedirect', { skip: isRsvpGuestFlowAccommodationsStepWithAccomsExperimentEnabled });
  const enableAccommodationsRedirectExperiment = redirectExperiment === 'treatment';

  const isAccommodationsEnabled = useMemo(() => {
    const accommodationsPage = data?.eventByName?.info?.event?.pagesInfo?.find(customPage => customPage.page.pageSlug === 'accommodations');
    return isEventPageVisible(accommodationsPage?.page.visibility);
  }, [data?.eventByName?.info?.event?.pagesInfo]);

  const isTravelEnabled = useMemo(() => {
    const travelPage = travelData?.eventByName?.info.event.pagesInfo?.[0];
    return isEventPageVisible(travelPage?.visibility);
  }, [travelData?.eventByName?.info.event.pagesInfo]);

  const isRegistryEnabled = useMemo(() => {
    const registryPages = data?.eventByName?.info?.event?.registryPages;
    const registryPage = Array.isArray(registryPages) && registryPages.length > 0 ? registryPages[0] : undefined;
    return isEventPageVisible(registryPage?.page.visibility);
  }, [data?.eventByName?.info?.event?.registryPages]);

  const enableRegistryRedirectInMessaging =
    useFeatureValue('enableRegistryRedirectInMessaging', {
      skip: !(noRSVP && !hideRegistryInMessaging && isRegistryEnabled),
    }).value === 'on';

  const accommodationsCardVisibilityData = {
    isAccommodationsPageEnabled: isAccommodationsEnabled,
  };

  const showRegistry = !hideRegistryInMessaging && isRegistryEnabled;
  const shouldRedirectToRegistryOnDecline = showRegistry && noRSVP && enableRegistryRedirectInMessaging;
  const shouldRedirectToAccommodationsOrTravel = didRSVP && (isAccommodationsEnabled || isTravelEnabled) && enableAccommodationsRedirectExperiment;
  const shouldRedirect = shouldRedirectToRegistryOnDecline || shouldRedirectToAccommodationsOrTravel || customRedirectUrl;
  const shouldRedirectToAccommodations = shouldRedirectToAccommodationsOrTravel && isAccommodationsEnabled;
  const shouldRedirectToTravel = shouldRedirectToAccommodationsOrTravel && !shouldRedirectToAccommodations;

  const isLoading =
    loading ||
    isRsvpCongratulationsEcardUserIsInUSFlagLoading ||
    isRsvpCongratulationsEcardExperimentEnabledFlagLoading ||
    isRsvpCongratulationsEventHandleExcludeListFlagLoading ||
    accomodationsLoading ||
    travelLoading ||
    !eventAccommodationsCheckReturn.isResolved ||
    registryLoading ||
    messagingSettingsLoading;

  const skipToSuccess =
    !rsvpCongratulationsEcardExperimentEnabled ||
    fromGiftWrap ||
    !rsvpCongratulationsEcardUserIsInUS ||
    (rsvpCongratulationsEventHandleExcludeListFlagPayload as string[])?.includes(eventHandle);

  return {
    data,
    shouldRedirect,
    shouldRedirToAccommodations: shouldRedirectToAccommodations,
    shouldRedirToTravel: shouldRedirectToTravel,
    shouldRedirectToRegistryOnDecline,
    isLoading,
    skipToSuccess,
    fromGiftWrap,
    accommodationsCardVisibilityData,
    guestInfo,
    eventId,
    shouldSkipAccommodationsPushStep: accommodationsCohort === 'hasOwnAccommodations' && !isRsvpGuestFlowAccommodationsStepWithAccomsExperimentEnabled,
    customRedirectUrl,
    showRegistry,
  };
};
